import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputText, Button, TextValueAccessor } from '@shui/angular';
import { catchError, EMPTY, tap } from 'rxjs';
import { AgGridAngular } from 'ag-grid-angular';
import { NumberInputCellComponent, shsThemeDark } from '@shs-digi-care-monorepo/frontend-lib';
import type { CellValueChangedEvent, ColDef } from 'ag-grid-community';
import { createStandardDealFormGroup, StandardDealFormGroup } from '../../models/standard-deal.model';
import { OneDxCustomerInformation, OneDxMaterialItem } from '../../../shared/models/onedx.model';
import { OneDxPlatformService } from '../../../shared/services/onedx-platform.service';

@Component({
    selector: 'app-standard-deal',
    templateUrl: './standard-deal.component.html',
    imports: [TranslateModule, ReactiveFormsModule, InputText, Button, TextValueAccessor, AgGridAngular],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class StandardDealForm {
    public showContinue = input<boolean>(true);
    public continue = output<void>();

    private oneDxService = inject(OneDxPlatformService);
    private destroyRef = inject(DestroyRef);
    private translate = inject(TranslateService);

    public formGroup: FormGroup<StandardDealFormGroup> = createStandardDealFormGroup();

    protected loading = signal(false);
    public customerInformation = signal<OneDxCustomerInformation | null>(null);
    public rowData = signal<OneDxMaterialItem[]>([]);

    protected canQuery = (): boolean => {
        const { quoteNumber } = this.formGroup.value;
        return !!quoteNumber;
    };

    protected queryOneDx(): void {
        if (!this.canQuery()) return;

        this.loading.set(true);
        this.oneDxService
            .getDealInformationByQuoteNumber({
                quoteNumber: this.formGroup.value.quoteNumber ?? ''
            })
            .pipe(
                tap((data) => {
                    this.customerInformation.set(data.customerInformation);
                    this.rowData.set(data.materials);
                    this.formGroup.controls.materials.setValue([...data.materials]);
                    this.loading.set(false);
                }),
                catchError(() => {
                    this.loading.set(false);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    protected colDefs: ColDef[] = [
        { field: 'productLine', headerName: this.translate.instant('iro.grid.product-line') },
        { field: 'description', headerName: this.translate.instant('iro.grid.description') },
        { field: 'smn', headerName: this.translate.instant('iro.grid.smn') },
        { field: 'kitSizeDescription', headerName: this.translate.instant('iro.grid.kit-size-description') },
        { field: 'kitSize', headerName: this.translate.instant('iro.grid.kit-size') },
        {
            field: 'annualTestVolume',
            headerName: this.translate.instant('iro.grid.annual-test-volume'),
            cellRenderer: NumberInputCellComponent,
            cellStyle: {
                padding: '0'
            }
        }
    ];

    protected gridOptions = {
        theme: shsThemeDark
    };

    protected onContinue(): void {
        this.continue.emit();
    }

    protected onCellValueChanged(event: CellValueChangedEvent): void {
        const allRowData: OneDxMaterialItem[] = [];
        event.api.forEachNode((node) => allRowData.push(node.data));
        this.formGroup.controls.materials.setValue(allRowData);
    }
}
